import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import { uploadAssets } from '../../../utils/uploadAssets';
import { DocumentType } from '../../../enum';
import { getAssetURl } from '../../../utils/url';
import { CURRENCY } from '../../../constants';
import { fCurrencyQAR } from '../../../utils/formatNumber';
import {
  BundleVariantGroup,
  BundleVariantOption,
  createLocalId,
} from './types';
import {
  getMaxSelectionFieldError,
  getVariantRequirementHint,
} from './variantValidation';

interface Props {
  variantGroups: BundleVariantGroup[];
  onChange: (groups: BundleVariantGroup[]) => void;
  accessToken: string;
  assetUrl: string;
  showVariantErrors?: boolean;
  disabled?: boolean;
  overridePrice: string;
  onOverridePriceChange: (value: string) => void;
  finalPrice: number;
}

export type VariantsComponentsTabHandle = {
  /** Validates max-selection drafts; does not save over product count. */
  prepareForSave: () =>
    | { ok: true; groups: BundleVariantGroup[] }
    | { ok: false; error: string };
};

const VariantsComponentsTab = forwardRef<VariantsComponentsTabHandle, Props>(
  function VariantsComponentsTab(
    {
      variantGroups,
      onChange,
      accessToken,
      assetUrl,
      showVariantErrors = false,
      disabled = false,
      overridePrice,
      onOverridePriceChange,
      finalPrice,
    },
    ref
  ) {
  const [newVariantEn, setNewVariantEn] = useState('');
  const [newVariantAr, setNewVariantAr] = useState('');
  /** Local draft so typing "2" after "1" does not become "12" → clamped to product count. */
  const [maxSelectionDrafts, setMaxSelectionDrafts] = useState<
    Record<string, string>
  >({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useImperativeHandle(ref, () => ({
    prepareForSave: () => {
      for (const group of variantGroups) {
        if (group.selectionType !== 'multiple') continue;

        const raw = (
          maxSelectionDrafts[group.id] ?? String(group.maxSelections ?? '')
        ).trim();
        const optionCount = group.options.length;
        const groupName = group.labelEn?.trim() || 'this variant group';

        if (optionCount === 0) {
          return {
            ok: false as const,
            error: `"${groupName}" — Add products before setting a Maximum Selection limit.`,
          };
        }

        if (raw === '' || !/^\d+$/.test(raw)) {
          return {
            ok: false as const,
            error: `"${groupName}" — Enter a Maximum Selection Limit (at least 1).`,
          };
        }

        const value = Math.floor(Number(raw));
        if (!Number.isFinite(value) || value < 1) {
          return {
            ok: false as const,
            error: `"${groupName}" — Enter a Maximum Selection Limit (at least 1).`,
          };
        }

        if (value > optionCount) {
          return {
            ok: false as const,
            error: `"${groupName}" — Maximum Selection cannot be more than ${optionCount} products.`,
          };
        }
      }

      const groups = variantGroups.map((group) => {
        if (group.selectionType !== 'multiple') return group;
        const raw = (
          maxSelectionDrafts[group.id] ?? String(group.maxSelections ?? 1)
        ).trim();
        const value = Math.floor(Number(raw));
        if (!Number.isFinite(value) || value < 1) return group;
        return { ...group, maxSelections: value };
      });

      setMaxSelectionDrafts({});
      return { ok: true as const, groups };
    },
  }));

  const totalOptions = variantGroups.reduce(
    (sum, group) => sum + group.options.length,
    0
  );

  const updateGroup = (
    groupId: string,
    updater: (group: BundleVariantGroup) => BundleVariantGroup
  ) => {
    onChange(
      variantGroups.map((group) =>
        group.id === groupId ? updater(group) : group
      )
    );
  };

  const handleAddVariant = () => {
    if (disabled || !newVariantEn.trim()) return;

    onChange([
      ...variantGroups,
      {
        id: createLocalId(),
        labelEn: newVariantEn.trim(),
        labelAr: newVariantAr.trim(),
        isRequired: false,
        selectionType: 'single',
        maxSelections: 1,
        activeState: true,
        expanded: true,
        options: [],
      },
    ]);

    setNewVariantEn('');
    setNewVariantAr('');
  };

  const handleRemoveGroup = (groupId: string) => {
    onChange(variantGroups.filter((group) => group.id !== groupId));
  };

  const handleAddOption = (groupId: string) => {
    updateGroup(groupId, (current) => ({
      ...current,
      options: [
        ...current.options,
        {
          id: createLocalId(),
          labelEn: '',
          labelAr: '',
          previewUrl: '',
          qty: 1,
          price: 0,
        },
      ],
    }));
  };

  const handleUpdateOption = (
    groupId: string,
    optionId: string,
    field: keyof BundleVariantOption,
    value: string | number
  ) => {
    updateGroup(groupId, (group) => ({
      ...group,
      options: group.options.map((option) =>
        option.id === optionId ? { ...option, [field]: value } : option
      ),
    }));
  };

  const applyRequirementChange = (
    groupId: string,
    updates: Partial<
      Pick<BundleVariantGroup, 'isRequired' | 'selectionType' | 'maxSelections'>
    >
  ) => {
    updateGroup(groupId, (current) => {
      const next = { ...current, ...updates };

      // Switching to Single always forces max = 1
      if (updates.selectionType === 'single') {
        next.maxSelections = 1;
        return next;
      }

      // Switching to Multiple: default max to 2 only when leaving Single.
      // Do NOT treat an intentional max of 1 as "coming from single".
      if (updates.selectionType === 'multiple') {
        const optionCount = next.options.length;
        if (current.selectionType === 'single') {
          next.maxSelections =
            optionCount > 0 ? Math.min(2, optionCount) : 2;
        } else {
          const currentMax = Number(next.maxSelections);
          if (!Number.isFinite(currentMax) || currentMax < 1) {
            next.maxSelections =
              optionCount > 0 ? Math.min(2, optionCount) : 2;
          }
          // Do not silent-clamp an over-limit max to product count — keep it
          // so validation can block save.
        }
        return next;
      }

      // maxSelections / isRequired only — apply value as entered when valid
      if (updates.maxSelections != null) {
        const max = Number(updates.maxSelections);
        if (!Number.isFinite(max) || max < 1) {
          next.maxSelections = 1;
        } else {
          next.maxSelections = Math.floor(max);
        }
      }

      return next;
    });
  };

  const commitMaxSelection = (
    groupId: string,
    raw: string,
    optionCount: number
  ) => {
    const trimmed = raw.trim();
    const clearDraft = () =>
      setMaxSelectionDrafts((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });

    if (trimmed === '' || !/^\d+$/.test(trimmed)) {
      applyRequirementChange(groupId, { maxSelections: 1 });
      clearDraft();
      return;
    }

    const value = Math.floor(Number(trimmed));
    if (!Number.isFinite(value) || value < 1) {
      applyRequirementChange(groupId, { maxSelections: 1 });
      clearDraft();
      return;
    }

    if (optionCount === 0) {
      toast.error('Add products before setting a Maximum Selection limit.');
      clearDraft();
      return;
    }

    if (value > optionCount) {
      toast.error(
        `Maximum Selection cannot be more than ${optionCount} products.`
      );
      applyRequirementChange(groupId, { maxSelections: optionCount });
      clearDraft();
      return;
    }

    applyRequirementChange(groupId, { maxSelections: value });
    clearDraft();
  };

  const handleRemoveOption = (groupId: string, optionId: string) => {
    updateGroup(groupId, (group) => {
      const options = group.options.filter((option) => option.id !== optionId);
      const max = Number(group.maxSelections);
      return {
        ...group,
        options,
        maxSelections:
          group.selectionType === 'multiple' &&
          Number.isFinite(max) &&
          options.length > 0 &&
          max > options.length
            ? options.length
            : group.maxSelections,
      };
    });
  };

  const handleOptionImageUpload = async (
    groupId: string,
    optionId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadResult: any = await uploadAssets(
      file,
      DocumentType.AddonProduct,
      accessToken
    );

    if (uploadResult?.data?.keypath) {
      handleUpdateOption(
        groupId,
        optionId,
        'previewUrl',
        uploadResult.data.keypath
      );
    }

    event.target.value = '';
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {disabled && (
        <Alert
          severity='info'
          sx={{ mb: 2 }}
        >
          Bundle Components are in use. Remove all bundle products to add
          variants.
        </Alert>
      )}

      <Box
        sx={{
          opacity: disabled ? 0.55 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
      <Box
        sx={{
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, mb: 0.5 }}
        >
          How variants work
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Create a variant group (e.g. Flower Bouquet) and add product options
          inside it. Use Selection Requirement and Selection Type to control
          whether the customer must choose and whether they pick one or many.
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 2,
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#FAF7F4',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 4 },
        }}
      >
        <Box>
          <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
            Bundle Price{' '}
            <Typography
              component='span'
              variant='caption'
              sx={{ color: '#d96c06ff' }}
            >
              (optional)
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              {CURRENCY}
            </Typography>
            <TextField
              size='small'
              value={overridePrice}
              placeholder='0'
              onChange={(e) => onOverridePriceChange(e.target.value)}
              InputProps={{ inputComponent: NumericFormatCustom as any }}
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'common.white',
                },
              }}
            />
          </Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 0.75 }}
          >
            {variantGroups.length} variant group
            {variantGroups.length === 1 ? '' : 's'} · {totalOptions} option
            {totalOptions === 1 ? '' : 's'} 
          </Typography>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            textAlign: { xs: 'left', sm: 'right' },
            minWidth: 100,
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Final Price
          </Typography>
          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 0.5 }}>
            {fCurrencyQAR(finalPrice)}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2}>
        {variantGroups.map((group, index) => (
          <Box
            key={group.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              minWidth: 0,
              width: '100%',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={1}
              sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: '#FDF0F1', minWidth: 0 }}
            >
              <Stack
                direction='row'
                spacing={1.5}
                alignItems='center'
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#EE3750',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {index + 1}
                </Box>
                <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {group.labelEn || 'New Variant Group'}
                </Typography>
                {group.labelAr && (
                  <Typography sx={{ fontWeight: 400, color: 'text.secondary' }}>
                    {group.labelAr}
                  </Typography>
                )}
                <Typography
                  variant='caption'
                  sx={{
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 10,
                    bgcolor: 'common.white',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    ml: 1,
                  }}
                >
                  {group.options.length} product{group.options.length === 1 ? '' : 's'}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 10,
                    bgcolor: group.isRequired ? '#fee2e2' : '#d1fae5',
                    color: group.isRequired ? '#ef4444' : '#10b981',
                    fontWeight: 600,
                  }}
                >
                  {group.isRequired ? 'Required' : 'Optional'}
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 10,
                    bgcolor: '#e0f2fe',
                    color: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {group.selectionType === 'single' ? (
                    <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <CheckBoxIcon sx={{ fontSize: 14 }} />
                  )}
                  <Typography variant='caption' sx={{ fontWeight: 600 }}>
                    {group.selectionType === 'single' ? 'Single' : 'Multiple'}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction='row'
                spacing={0.5}
              >
                <IconButton
                  size='small'
                  onClick={() => handleRemoveGroup(group.id)}
                  sx={{ color: '#ef4444' }}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() =>
                    updateGroup(group.id, (current) => ({
                      ...current,
                      expanded: !current.expanded,
                    }))
                  }
                  sx={{ color: '#EE3750' }}
                >
                  {group.expanded ? (
                    <ExpandLessIcon fontSize='small' />
                  ) : (
                    <ExpandMoreIcon fontSize='small' />
                  )}
                </IconButton>
              </Stack>
            </Stack>

            <Collapse in={group.expanded}>
              <Box sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0, overflowX: 'hidden' }}>
                <Grid
                  container
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      fullWidth
                      label='Variant Name (EN)'
                      value={group.labelEn}
                      onChange={(e) =>
                        updateGroup(group.id, (current) => ({
                          ...current,
                          labelEn: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      fullWidth
                      label='Variant Name (AR)'
                      value={group.labelAr}
                      onChange={(e) =>
                        updateGroup(group.id, (current) => ({
                          ...current,
                          labelAr: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 2, sm: 3 }}
                    alignItems='flex-start'
                    sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
                  >
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Selection Requirement
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={group.isRequired ? 'required' : 'optional'}
                        onChange={(_, value) => {
                          if (!value) return;
                          applyRequirementChange(group.id, {
                            isRequired: value === 'required',
                          });
                        }}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          '& .MuiToggleButton-root': {
                            bgcolor: 'common.white',
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: 'divider',
                            textTransform: 'none',
                            px: 3,
                          },
                          '& .MuiToggleButton-root[value="optional"].Mui-selected': {
                            bgcolor: '#16a34a',
                            color: 'common.white',
                            '&:hover': { bgcolor: '#15803d' },
                          },
                          '& .MuiToggleButton-root[value="required"].Mui-selected': {
                            bgcolor: '#dc2626',
                            color: 'common.white',
                            '&:hover': { bgcolor: '#b91c1c' },
                          },
                        }}
                      >
                        <ToggleButton value='optional'>Optional</ToggleButton>
                        <ToggleButton value='required'>Required</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Selection Type
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={group.selectionType}
                        onChange={(_, value) => {
                          if (!value) return;
                          applyRequirementChange(group.id, {
                            selectionType: value,
                          });
                        }}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          '& .MuiToggleButton-root': {
                            bgcolor: 'common.white',
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: 'divider',
                            textTransform: 'none',
                            px: 2,
                            gap: 1,
                          },
                          '& .MuiToggleButton-root.Mui-selected': {
                            bgcolor: '#EE3750',
                            color: 'common.white',
                            '&:hover': { bgcolor: '#EE3750' },
                          },
                        }}
                      >
                        <ToggleButton value='single'>
                          <RadioButtonCheckedIcon sx={{ fontSize: 16 }} />
                          Single
                        </ToggleButton>
                        <ToggleButton value='multiple'>
                          <CheckBoxIcon sx={{ fontSize: 16 }} />
                          Multiple
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {group.selectionType === 'multiple' ? (
                      <Box sx={{ width: { xs: '100%', sm: 220 }, maxWidth: 260 }}>
                        <Typography variant='caption' color='text.secondary'>
                          Maximum Selection
                        </Typography>
                        {(() => {
                          const optionCount = group.options.length;
                          const draftValue =
                            maxSelectionDrafts[group.id] ??
                            String(group.maxSelections ?? 1);
                          const draftNumber = Number(draftValue);
                          const draftOverLimit =
                            draftValue !== '' &&
                            /^\d+$/.test(draftValue) &&
                            optionCount > 0 &&
                            draftNumber > optionCount;
                          const maxSelectionError = draftOverLimit
                            ? `Maximum Selection cannot be more than ${optionCount} products.`
                            : getMaxSelectionFieldError(group);
                          const showMaxError =
                            !!maxSelectionError &&
                            (draftOverLimit ||
                              showVariantErrors ||
                              (group.maxSelections != null &&
                                optionCount > 0 &&
                                Number(group.maxSelections) > optionCount));

                          return (
                            <TextField
                              size='small'
                              type='text'
                              inputMode='numeric'
                              hiddenLabel
                              value={draftValue}
                              onFocus={() => {
                                setMaxSelectionDrafts((prev) => ({
                                  ...prev,
                                  [group.id]: String(group.maxSelections ?? 1),
                                }));
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '' || /^\d+$/.test(raw)) {
                                  const val = Number(raw);
                                  if (raw !== '' && optionCount > 0 && val > optionCount) {
                                    toast.error(
                                      `Maximum Selection cannot be more than ${optionCount} products.`
                                    );
                                    applyRequirementChange(group.id, { maxSelections: optionCount });
                                    setMaxSelectionDrafts((prev) => ({
                                      ...prev,
                                      [group.id]: String(optionCount),
                                    }));
                                  } else {
                                    setMaxSelectionDrafts((prev) => ({
                                      ...prev,
                                      [group.id]: raw,
                                    }));
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                commitMaxSelection(
                                  group.id,
                                  e.target.value,
                                  optionCount
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              placeholder='e.g. 3'
                              error={showMaxError}
                              helperText={
                                showMaxError ? maxSelectionError : undefined
                              }
                              FormHelperTextProps={{
                                sx: {
                                  m: 0,
                                  mt: 0.5,
                                  lineHeight: 1.3,
                                  whiteSpace: 'normal',
                                },
                              }}
                              sx={{
                                mt: 0.5,
                                display: 'flex',
                                width: 100,
                                bgcolor: 'common.white',
                                '& .MuiOutlinedInput-root': {
                                  height: 36,
                                },
                              }}
                            />
                          );
                        })()}
                      </Box>
                    ) : null}
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: { xs: 1, sm: 3 },
                      pb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: group.isRequired ? '#ef4444' : '#4ade80',
                        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                      }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      {getVariantRequirementHint(group)}
                    </Typography>
                  </Box>
                </Box>



                {group.options.length > 0 ? (
                  <TableContainer
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                  <Table
                    size='small'
                    sx={{ minWidth: 640 }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          '& th': {
                            borderBottom: 'none',
                            bgcolor: '#FDF0F1',
                            color: '#EE3750',
                            fontWeight: 600,
                            py: 1.5,
                            whiteSpace: 'nowrap',
                          },
                          '& th:first-of-type': {
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                          },
                          '& th:last-of-type': {
                            borderTopRightRadius: 8,
                            borderBottomRightRadius: 8,
                          },
                        }}
                      >
                        <TableCell>Images</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>Product Name (AR)</TableCell>
                        <TableCell align='center'>Price ({CURRENCY})</TableCell>
                        <TableCell align='center'>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.options.map((option) => (
                        <TableRow key={option.id}>
                          <TableCell>
                            <Box sx={{ position: 'relative', width: 48, height: 48 }}>
                              <Box
                                onClick={() =>
                                  fileInputRefs.current[option.id]?.click()
                                }
                                sx={{
                                  width: 48,
                                  height: 48,
                                  border: '1px dashed',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  overflow: 'hidden',
                                }}
                              >
                                {option.previewUrl ? (
                                  <Box
                                    component='img'
                                    src={getAssetURl(option.previewUrl, assetUrl)}
                                    alt={option.labelEn}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <AddIcon fontSize='small' />
                                )}
                              </Box>
                              {option.previewUrl ? (
                                <IconButton
                                  size='small'
                                  aria-label='Remove image'
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleUpdateOption(
                                      group.id,
                                      option.id,
                                      'previewUrl',
                                      ''
                                    );
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: -6,
                                    right: -6,
                                    width: 20,
                                    height: 20,
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    color: 'common.white',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                                    '& .MuiSvgIcon-root': { fontSize: 12 },
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              ) : null}
                            </Box>
                            <input
                              ref={(element) => {
                                fileInputRefs.current[option.id] = element;
                              }}
                              type='file'
                              accept='image/png,image/jpeg,image/jpg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif'
                              hidden
                              onChange={(event) =>
                                handleOptionImageUpload(
                                  group.id,
                                  option.id,
                                  event
                                )
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <TextField
                              size='small'
                              fullWidth
                              placeholder='Product Name'
                              value={option.labelEn}
                              onChange={(e) =>
                                handleUpdateOption(
                                  group.id,
                                  option.id,
                                  'labelEn',
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <TextField
                              size='small'
                              fullWidth
                              placeholder='Product Name (AR)'
                              value={option.labelAr}
                              onChange={(e) =>
                                handleUpdateOption(
                                  group.id,
                                  option.id,
                                  'labelAr',
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <TextField
                              size='small'
                              value={option.price}
                              onChange={(e) =>
                                handleUpdateOption(
                                  group.id,
                                  option.id,
                                  'price',
                                  Number(e.target.value) || 0
                                )
                              }
                              sx={{ width: 96 }}
                              InputProps={{
                                inputComponent: NumericFormatCustom as any,
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              size='small'
                              onClick={() =>
                                handleRemoveOption(group.id, option.id)
                              }
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant='body2' color='text.secondary'>
                      No products yet. Click "+ Add Product" to add items.
                    </Typography>
                  </Box>
                )}

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={() => handleAddOption(group.id)}
                  sx={{
                    mt: 2,
                    bgcolor: '#EE3750',
                    color: 'common.white',
                    textTransform: 'none',
                    border: 'none',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      bgcolor: '#EE3750',
                      boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  Add Product
                </Button>
              </Box>
            </Collapse>
          </Box>
        ))}
      </Stack>

      <Box
        sx={{
          mt: 3,
          p: 2.5,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant='body2' sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
          Add New Variant Group
        </Typography>
        <Grid
          container
          spacing={2}
          alignItems='flex-end'
        >
          <Grid
            item
            xs={12}
            md={5}
          >
            <Typography variant='body2' sx={{ mb: 0.5, color: 'text.secondary' }}>
              Variant Name (EN) <Typography component='span' sx={{ color: 'error.main' }}>*</Typography>
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder='e.g. Flower Bouquet, Cake Size'
              value={newVariantEn}
              onChange={(e) => setNewVariantEn(e.target.value)}
              sx={{ bgcolor: 'common.white' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
          >
            <Typography variant='body2' sx={{ mb: 0.5, color: 'text.secondary' }}>
              Variant Name (AR)
            </Typography>
            <TextField
              fullWidth
              size='small'
              value={newVariantAr}
              onChange={(e) => setNewVariantAr(e.target.value)}
              sx={{ bgcolor: 'common.white' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
          >
            <Button
              fullWidth
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAddVariant}
              sx={{
                bgcolor: '#EE3750',
                color: 'common.white',
                textTransform: 'none',
                border: 'none',
                height: 40,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  bgcolor: '#EE3750',
                  boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
                },
              }}
            >
              Add Variant
            </Button>
          </Grid>
        </Grid>
      </Box>
      </Box>
    </Box>
  );
  }
);

export default VariantsComponentsTab;
